import { useState, useEffect, useContext } from 'react';
import { Box, Button, TextField, Switch, FormControlLabel, Typography, Checkbox, Modal } from '@mui/material';
import { getMaintenanceBannerDetails, putMaintenanceBannerDetails } from '../../api/settings';
import { StoreContext } from '../../components/Store';
import { dateFormatChange } from '../../utils/date';
import DOMPurify from 'dompurify';

const MAX_BANNER_TEXT_LENGTH = 200;
const MAX_MORE_INFO_LENGTH = 800;

const MaintenanceBannerDetails = () => {
  const store = useContext(StoreContext);

  const [showPreview, setShowPreview] = useState(false);
  const [disableSave, setDisableSave] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [errors, setErrors] = useState({});

  const htmlSanitizeConfig = {
    ALLOWED_TAGS: ['b', 'i', 'em', 'u', 'strong', 'a', 'p', 'br'],
    ALLOWED_ATTR: ['href', 'target'],
  };

  const [banner, setBanner] = useState({
    enabled: false,
    bannerText: '',
    moreInfo: '',
    startDate: null,
    endDate: null,
    visibility: '',
    lastModifiedUser: '',
    lastModifiedDate: null,
  });

  useEffect(() => {
    const fetchBannerDetails = async () => {
      try {
        const data = await getMaintenanceBannerDetails();
        if (data) {
          // Convert ISO date strings to Date objects
          const startDate = data.startDate ? new Date(data.startDate) : null;
          const endDate = data.endDate ? new Date(data.endDate) : null;

          setBanner({
            ...data,
            startDate,
            endDate,
            lastModifiedDate: data.lastModified,
          });
        }
      } catch (error) {
        console.error('Error fetching maintenance banner details:', error);
      }
    };

    fetchBannerDetails();
  }, []);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setBanner((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Clear error for this field
    setErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
    setHasSaved(false);
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    const currentDate = banner[name] || new Date();
    const [year, month, day] = value.split('-');
    const newDate = new Date(currentDate);
    newDate.setFullYear(year, month - 1, day);

    setBanner((prev) => ({
      ...prev,
      [name]: newDate,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
    setHasSaved(false);
  };

  const handleTimeChange = (e) => {
    const { name, value } = e.target;
    const currentDate = banner[name] || new Date();
    const [hours, minutes] = value.split(':');
    const newDate = new Date(currentDate);
    newDate.setHours(hours, minutes);

    setBanner((prev) => ({
      ...prev,
      [name]: newDate,
    }));
    setHasSaved(false);
  };

  const handleClientChange = (type, checked) => {
    let newVisibility = banner.visibility;
    const currentVisibilityLower = (banner.visibility || '').toLowerCase();

    if (type === 'internal' && checked) {
      newVisibility = currentVisibilityLower === 'external' ? 'All' : 'Internal';
    } else if (type === 'internal' && !checked) {
      newVisibility = currentVisibilityLower === 'all' ? 'External' : '';
    } else if (type === 'external' && checked) {
      newVisibility = currentVisibilityLower === 'internal' ? 'All' : 'External';
    } else if (type === 'external' && !checked) {
      newVisibility = currentVisibilityLower === 'all' ? 'Internal' : '';
    }

    setBanner((prev) => ({
      ...prev,
      visibility: newVisibility,
    }));
    setHasSaved(false);
  };

  const clearValue = (field) => {
    setBanner((prev) => ({
      ...prev,
      [field]: '',
    }));
    setHasSaved(false);
  };

  const validate = () => {
    const newErrors = {};

    if (!banner.bannerText || banner.bannerText.trim() === '') {
      newErrors.bannerText = 'Banner text is required';
    } else if (banner.bannerText.length > MAX_BANNER_TEXT_LENGTH) {
      newErrors.bannerText = `Banner text must not exceed ${MAX_BANNER_TEXT_LENGTH} characters`;
    }

    if (banner.moreInfo && banner.moreInfo.length > MAX_MORE_INFO_LENGTH) {
      newErrors.moreInfo = `More info must not exceed ${MAX_MORE_INFO_LENGTH} characters`;
    }

    if (!banner.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!banner.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (banner.startDate && banner.endDate && banner.startDate >= banner.endDate) {
      newErrors.endDate = 'End date must be after start date';
    }

    if (!banner.visibility || banner.visibility === '') {
      newErrors.visibility = 'Please select at least one audience';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveBannerDetails = async () => {
    if (!validate()) {
      return;
    }

    setDisableSave(true);
    setHasSaved(false);

    try {
      // Convert dates to ISO strings for API
      const dataToSave = {
        ...banner,
        startDate: banner.startDate ? banner.startDate.toISOString() : null,
        endDate: banner.endDate ? banner.endDate.toISOString() : null,
      };

      await putMaintenanceBannerDetails(dataToSave);
      setHasSaved(true);

      // Trigger refresh of the banner display
      if (store.maintenanceRefresh && store.maintenanceRefresh[1]) {
        store.maintenanceRefresh[1](true);
      }
    } catch (error) {
      console.error('Error saving maintenance banner details:', error);
      setErrors({ general: 'Failed to save maintenance banner details' });
    } finally {
      setDisableSave(false);
    }
  };

  return (
    <>
      {/* Preview Modal */}
      <Modal open={showPreview} onClose={() => setShowPreview(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 1000,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 0,
            borderRadius: 2,
            maxHeight: '90vh',
            overflow: 'auto',
          }}
        >
          <Box
            sx={{
              p: 3,
              borderBottom: '1px solid #ddd',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">Banner Preview</Typography>
            <Button variant="contained" onClick={() => setShowPreview(false)}>
              Close Preview
            </Button>
          </Box>

          {/* Show the banner as it would appear */}
          <Box sx={{ bgcolor: '#f3f3f3', p: 2, borderBottom: '1px solid #ddd' }}>
            <Typography sx={{ color: '#c41e3a', fontWeight: 'bold', textAlign: 'center' }}>
              {banner.bannerText || 'No banner text entered'}
            </Typography>
          </Box>

          {banner.moreInfo && (
            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                More Info Content:
              </Typography>
              <Box
                sx={{ whiteSpace: 'pre-wrap' }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(banner.moreInfo, htmlSanitizeConfig) }}
              />
            </Box>
          )}
        </Box>
      </Modal>

      <Box className="semi-fluid-container container-lg" sx={{ my: 4 }}>
        <Box className="floating-box-newload" sx={{ p: 4 }}>
          <Box
            className="maintenance-details-head"
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}
          >
            <Typography variant="h4" className="floating-box-header-newload">
              Maintenance Banner
            </Typography>
            <Button variant="contained" onClick={() => setShowPreview(true)} sx={{ width: '12rem' }}>
              Show Preview
            </Button>
          </Box>

          <Box className="maintenance-details-body">
            <Box className="maintenance-section" sx={{ mb: 3 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                name="bannerText"
                label="Banner Text"
                placeholder="Enter Text"
                value={banner.bannerText}
                onChange={handleChange}
                error={!!errors.bannerText}
                helperText={errors.bannerText}
                inputProps={{ maxLength: MAX_BANNER_TEXT_LENGTH }}
              />
              <Box className="maintenance-chars" sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="caption" className="char-count">
                  Char count {banner.bannerText.length} / {MAX_BANNER_TEXT_LENGTH}
                </Typography>
                <Button size="small" onClick={() => clearValue('bannerText')}>
                  Clear
                </Button>
              </Box>
            </Box>

            <Box className="maintenance-section" sx={{ mb: 3 }}>
              <TextField
                fullWidth
                multiline
                rows={6}
                name="moreInfo"
                label="More Info"
                placeholder="Enter Text"
                value={banner.moreInfo}
                onChange={handleChange}
                error={!!errors.moreInfo}
                helperText={
                  errors.moreInfo ||
                  'When entered, this automatically adds link to the banner text, displayed in a modal'
                }
                inputProps={{ maxLength: MAX_MORE_INFO_LENGTH }}
              />
              <Box className="maintenance-chars" sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="caption" className="char-count">
                  Char count {banner.moreInfo.length} / {MAX_MORE_INFO_LENGTH}
                </Typography>
                <Button size="small" onClick={() => clearValue('moreInfo')}>
                  Clear
                </Button>
              </Box>
            </Box>

            <Box className="maintenance-section maintenance-details-dates" sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Box className="maintenance-details-date" sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Start Date & Time"
                  name="startDate"
                  type="date"
                  value={banner?.startDate?.toISOString().split('T')[0] || ''}
                  onChange={handleDateChange}
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  name="startDate"
                  type="time"
                  value={banner?.startDate?.toTimeString().slice(0, 5) || ''}
                  onChange={handleTimeChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box className="maintenance-details-date" sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="End Date & Time"
                  name="endDate"
                  type="date"
                  value={banner?.endDate?.toISOString().split('T')[0] || ''}
                  onChange={handleDateChange}
                  error={!!errors.endDate}
                  helperText={errors.endDate}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  name="endDate"
                  type="time"
                  value={banner?.endDate?.toTimeString().slice(0, 5) || ''}
                  onChange={handleTimeChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>

            <Box className="maintenance-section maintenance-details-clients" sx={{ mb: 3 }}>
              <Typography variant="subtitle1" className="maintenance-subheading" sx={{ mb: 1 }}>
                Which clients is the banner being displayed to?
              </Typography>
              {errors.visibility && (
                <Typography color="error" variant="caption" sx={{ display: 'block', mb: 1 }}>
                  {errors.visibility}
                </Typography>
              )}
              <Box className="client-checkboxes" sx={{ display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="internal"
                      checked={['all', 'internal', 'All', 'Internal'].includes(banner.visibility)}
                      onChange={(e) => handleClientChange('internal', e.target.checked)}
                    />
                  }
                  label="Internal"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="external"
                      checked={['all', 'external', 'All', 'External'].includes(banner.visibility)}
                      onChange={(e) => handleClientChange('external', e.target.checked)}
                    />
                  }
                  label="External"
                />
              </Box>
            </Box>

            <Box className="maintenance-details-enabled" sx={{ mb: 3 }}>
              <Typography variant="subtitle1" className="maintenance-subheading" sx={{ mb: 1 }}>
                Enabled / Disabled
              </Typography>
              <FormControlLabel
                control={<Switch name="enabled" checked={banner.enabled} onChange={handleChange} />}
                label={banner.enabled ? 'Enabled' : 'Disabled'}
              />
            </Box>
          </Box>

          <Box
            className="maintenance-details-footer"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 4,
              pt: 3,
              borderTop: '1px solid #ddd',
            }}
          >
            <Typography variant="body2">
              Last modified by: {banner?.lastModifiedUser || 'N/A'} on{' '}
              {banner?.lastModifiedDate ? dateFormatChange(banner.lastModifiedDate) : 'N/A'}
            </Typography>
            <Box>
              {hasSaved && (
                <Typography color="success.main" sx={{ display: 'inline', mr: 2, fontWeight: 'bold' }}>
                  Successfully saved.
                </Typography>
              )}
              {errors.general && (
                <Typography color="error" sx={{ display: 'inline', mr: 2 }}>
                  {errors.general}
                </Typography>
              )}
              <Button variant="contained" onClick={saveBannerDetails} disabled={disableSave}>
                Save
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default MaintenanceBannerDetails;
